import React, { useState } from 'react';
import { useBooking } from '../context/BookingContext';
import { calculateRoomPrice } from '../utils/pricingCalculator';
import { getShopId, getApiBaseUrl, getShopCode} from '../config';
const SHOP_ID = getShopId();
const API_BASE_URL = getApiBaseUrl();
const SHOP_CODE = getShopCode();
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// const SHOP_ID = process.env.REACT_APP_SHOP_ID;

const RateOption = ({ rate, room, currentQuantity, onCapacityError  }) => {
  const { state, dispatch, formatCurrency, calculateNights } = useBooking();

  // --- Practical & Activity Based Inclusions ---
  const getInclusions = (rateObj) => {
    if (rateObj && rateObj.inclusions !== undefined) {
      if (Array.isArray(rateObj.inclusions) && rateObj.inclusions.length > 0) {
        return rateObj.inclusions
          .map(inclusion => {
            if (typeof inclusion === 'string') return inclusion;
            if (typeof inclusion === 'object') return inclusion.title || inclusion.name;
            return String(inclusion);
          })
          .filter(title => title && title.trim() !== '');
      }
      if (Array.isArray(rateObj.inclusions) && rateObj.inclusions.length === 0) {
        return [];
      }
    }

    const name = rateObj.name ? rateObj.name.toLowerCase() : '';
    
    if (name.includes('ap')) {
      return [
        'All Meals Included (Breakfast, Lunch, Dinner)',
        'Gala Dinner included (Worth ₹2500)',
        'Comp. Airport Transfer (Two-way)',
        'Jeep Safari (Evening Slot)'
      ];
    } else if (name.includes('map') || name.includes('mep')) {
      return [
        'Breakfast & Dinner Buffet',
        'Bonfire & Musical Evening',
        'Guided Nature Walk / Jungle Trail',
        '15% Discount on Spa Services'
      ];
    } else if (name.includes('cp')) {
      return [
        'Buffet Breakfast',
        'Welcome Drink on Arrival (Non-Alcoholic)',
        'High Speed Wi-Fi',
        '2 Bottles Mineral Water Daily'
      ];
    } else if (name.includes('ep')) {
      return [
        'Room Only',
        'Access to Swimming Pool',
        'Gym / Fitness Centre Usage',
        'Free Parking'
      ];
    }
    
    return ['Complimentary Wi-Fi', 'Access to Hotel Amenities'];
  };

  const inclusions = getInclusions(rate);

  // --- Helper functions ---
  const getNextAvailableRoomNumber = () => {
    const usedRoomNumbers = state.cart.map(item => item.roomNumber);
    let nextRoomNumber = 1;
    while (usedRoomNumbers.includes(nextRoomNumber)) {
      nextRoomNumber++;
    }
    return nextRoomNumber;
  };

  const getGuestConfigForRoom = (roomNumber) => {
    const guestConfig = state.searchState.room_guest.find(
      guest => guest.room_no === roomNumber
    );
    return guestConfig || state.searchState.room_guest[0];
  };

  const getCartItemsForThisRate = () => {
    return state.cart.filter(item => 
      item.id === rate.id && item.roomId === room.id
    );
  };

  const getCartItemsForThisRoomCategory = () => {
    return state.cart.filter(item => 
      item.roomId === room.id
    );
  };

  const getCurrentRoomCountForThisRate = () => {
    return getCartItemsForThisRate().length;
  };

  const getCurrentRoomCountForThisCategory = () => {
    return getCartItemsForThisRoomCategory().length;
  };

  const currentRoomCountForThisRate = getCurrentRoomCountForThisRate();
  const currentRoomCountForThisCategory = getCurrentRoomCountForThisCategory();

  // Calculate room limits
  const totalRoomsAvailable = room.total_rooms || 0;
  const totalRoomsSelectedGlobally = state.cart.length;
  const maxRoomsAllowed = state.searchState.room_guest.length;
  
  const isCategoryLimitReached = currentRoomCountForThisCategory >= totalRoomsAvailable;
  const allRequiredRoomsSelected = totalRoomsSelectedGlobally >= maxRoomsAllowed;
  const roomsStillNeeded = Math.max(0, maxRoomsAllowed - totalRoomsSelectedGlobally);

  // --- Pricing Calculations ---
const getPricingForGuests = (adults, children, childAges = [], isFreeNightCalculation = false) => {
  const maxGuestsInRoom = room.maxGuests || 3;
  let baseAdults = 0;
  let extraAdults = 0;



   if (isFreeNightCalculation && rate.offer?.discount_form === 'free_nights') {
    return {
      originalPrice: rate.originalPrice,  // Original base price (1000)
      discountedPrice: rate.price,         // Average price after both discounts (500)
      hasDiscount: rate.originalPrice > rate.price,
      baseAdults: adults,
      extraAdults: 0,
      chargeableChildren: 0
    };
  }
  
  // Check if this is a free night offer
  const isFreeNightOffer = rate.offer && rate.offer.discount_form === 'free_nights';
  
  // **DEBUG: Log the rate structure to see child pricing**
  // console.log('Rate structure for child pricing:', {
  //   rateId: rate.id,
  //   rateName: rate.name,
  //   extra_child_pricing: rate.extra_child_pricing,
  //   child_pricing: rate.child_pricing,
  //   child_price: rate.child_price,
  //   child_original_price: rate.child_original_price
  // });
  
  const hasExactAdultPricing = rate.adult_pricing && 
    rate.adult_pricing.some(pricing => pricing.adults_count === adults);
  
  if (hasExactAdultPricing) {
    baseAdults = adults;
    extraAdults = 0;
  } else {
    const availableAdultCounts = rate.adult_pricing.map(p => p.adults_count).sort((a, b) => a - b);
    let closestAdultCount = 0;
    
    for (const count of availableAdultCounts) {
      if (count <= adults && count <= maxGuestsInRoom) {
        closestAdultCount = count;
      }
    }
    
    if (closestAdultCount === 0 && availableAdultCounts.length > 0) {
      closestAdultCount = Math.min(...availableAdultCounts);
    }
    
    baseAdults = Math.min(closestAdultCount, maxGuestsInRoom);
    extraAdults = Math.max(0, adults - baseAdults);
  }

  // Calculate chargeable children based on age
  let chargeableChildren = 0;
  
  if (children > 0 && childAges && childAges.length > 0) {
    for (let i = 0; i < Math.min(children, childAges.length); i++) {
      const age = childAges[i];
      if (age > 5) { // Children above 5 are chargeable
        chargeableChildren++;
      }
    }
    
    if (children > childAges.length) {
      const remainingChildren = children - childAges.length;
      chargeableChildren += remainingChildren;
    }
  } else {
    chargeableChildren = Math.min(children, 2);
  }
  
  chargeableChildren = Math.min(chargeableChildren, 2);
  
  // Calculate BOTH original and discounted prices
  let totalOriginalPrice = 0;
  let totalDiscountedPrice = 0;

  // Calculate BASE adult pricing
  if (rate.adult_pricing && Array.isArray(rate.adult_pricing)) {
    let adultPricing = rate.adult_pricing.find(pricing => pricing.adults_count === baseAdults);

    if (!adultPricing) {
      const sortedPricing = [...rate.adult_pricing].sort((a, b) => a.adults_count - b.adults_count);
      adultPricing = sortedPricing.reduce((prev, current) => {
        const prevDiff = Math.abs(prev.adults_count - baseAdults);
        const currentDiff = Math.abs(current.adults_count - baseAdults);
        return currentDiff < prevDiff ? current : prev;
      });
    }
    
    if (adultPricing) {
      totalOriginalPrice += adultPricing.original_price || adultPricing.price;
      totalDiscountedPrice += adultPricing.price;
    }
  }

  // Calculate EXTRA adult pricing
  if (extraAdults > 0 && rate.extra_bed_pricing && Array.isArray(rate.extra_bed_pricing) && rate.extra_bed_pricing.length > 0) {
    const extraBedPricing = rate.extra_bed_pricing[0];
    if (extraBedPricing) {
      const extraBedDiscountedPrice = extraBedPricing.price || 0;
      const extraBedOriginalPrice = extraBedPricing.original_price || extraBedDiscountedPrice;
      
      const extraAdultOriginal = extraBedOriginalPrice * extraAdults;
      const extraAdultDiscounted = extraBedDiscountedPrice * extraAdults;
      
      totalOriginalPrice += extraAdultOriginal;
      totalDiscountedPrice += extraAdultDiscounted;
    }
  }

  // **CRITICAL FIX: Calculate child pricing the SAME WAY as extra adults**
  if (chargeableChildren > 0) {
    // console.log(`Checking child pricing for ${chargeableChildren} chargeable children`);
    
    // First, try to find child pricing in the rate structure
    let childDiscountedPrice = 0;
    let childOriginalPrice = 0;
    
    // **METHOD 1: Check extra_child_pricing (same structure as extra_bed_pricing)**
    if (rate.extra_child_pricing && Array.isArray(rate.extra_child_pricing) && rate.extra_child_pricing.length > 0) {
      const childPricing = rate.extra_child_pricing[0];
      childDiscountedPrice = childPricing.price || 0;
      childOriginalPrice = childPricing.original_price || childDiscountedPrice;
      // console.log(`Found child pricing in extra_child_pricing: original=${childOriginalPrice}, discounted=${childDiscountedPrice}`);
    }
    // **METHOD 2: Check child_pricing array**
    else if (rate.child_pricing && Array.isArray(rate.child_pricing) && rate.child_pricing.length > 0) {
      const childPricing = rate.child_pricing[0];
      childDiscountedPrice = childPricing.price || 0;
      childOriginalPrice = childPricing.original_price || childDiscountedPrice;
      // console.log(`Found child pricing in child_pricing: original=${childOriginalPrice}, discounted=${childDiscountedPrice}`);
    }
    // **METHOD 3: Check direct properties**
    else if (rate.child_price !== undefined) {
      childDiscountedPrice = rate.child_price;
      childOriginalPrice = rate.child_original_price || rate.child_price || childDiscountedPrice;
      // console.log(`Found child pricing in direct properties: original=${childOriginalPrice}, discounted=${childDiscountedPrice}`);
    }
    // **METHOD 4: Check in adult_pricing for child rates**
    else if (rate.adult_pricing && rate.adult_pricing.length > 0) {
      // Sometimes child prices are in adult_pricing with adults_count = 0
      const childRate = rate.adult_pricing.find(p => p.adults_count === 0);
      if (childRate) {
        childDiscountedPrice = childRate.price || 0;
        childOriginalPrice = childRate.original_price || childDiscountedPrice;
        // console.log(`Found child pricing in adult_pricing (adults_count=0): original=${childOriginalPrice}, discounted=${childDiscountedPrice}`);
      }
    }
    // **METHOD 5: Default values from your example**
    else {
      // Based on your example: child original = 4000, child discounted = 3000
      childDiscountedPrice = 3000; 
      childOriginalPrice = 4000;
      // console.log(`Using default child pricing: original=${childOriginalPrice}, discounted=${childDiscountedPrice}`);
    }
    
    // **APPLY CHILD PRICING EXACTLY LIKE EXTRA ADULTS**
    if (childOriginalPrice > 0 || childDiscountedPrice > 0) {
      // For free night calculations, apply child charges to both original and discounted prices
      // Just like we do with extra adults
      const childOriginal = childOriginalPrice * chargeableChildren;
      const childDiscounted = childDiscountedPrice * chargeableChildren;
      
      totalOriginalPrice += childOriginal;
      totalDiscountedPrice += childDiscounted;
      
      // console.log(`Child pricing applied for ${chargeableChildren} children: Original+=${childOriginal}, Discounted+=${childDiscounted}`);
    } else {
      console.log(`WARNING: Child pricing not found or is 0 for rate ${rate.id}`);
    }
  }

  // console.log(`Total for ${adults}A ${children}C: Original=${totalOriginalPrice}, Discounted=${totalDiscountedPrice}, ExtraAdults=${extraAdults}, ChargeableChildren=${chargeableChildren}, isFreeNightCalc=${isFreeNightCalculation}`);

  return {
    originalPrice: totalOriginalPrice,
    discountedPrice: totalDiscountedPrice > 0 ? totalDiscountedPrice : 
                    calculateRoomPrice(rate, adults, chargeableChildren, extraAdults),
    hasDiscount: totalOriginalPrice > totalDiscountedPrice,
    baseAdults: baseAdults,
    extraAdults: extraAdults,
    chargeableChildren: chargeableChildren
  };
};
const calculateGuestBasedPricing = () => {
  const nextRoomNumber = getNextAvailableRoomNumber();
  const guestConfig = getGuestConfigForRoom(nextRoomNumber);
  
  const adultsCount = guestConfig.no_of_adults;
  const childrenCount = Math.min(guestConfig.no_of_children, 2);
  const childAges = guestConfig.child_ages || [];
  
  // console.log(`Guest config for room ${nextRoomNumber}: Adults=${adultsCount}, Children=${childrenCount}, ChildAges=${JSON.stringify(childAges)}`);
  
  return getPricingForGuests(adultsCount, childrenCount, childAges);
};

const guestPricing = calculateGuestBasedPricing();


  const calculatedPrice = guestPricing.discountedPrice;

  // --- Discount Calculations ---
  const getDiscountInfo = () => {
    if (rate.offer && rate.offer.discount_name) {
      const { id,discount_type, discount_value, discount_percentage, discount_amount } = rate.offer;
      
      if (discount_type === 'percentage') {
        return {
          name: rate.offer.discount_name,
          type: 'percentage',
          value: discount_percentage,
          rawValue: discount_value,
          amount: discount_amount,
          discountId: id // Include the discount ID
        };
      } else if (discount_type === 'fixed') {
        return {
          name: rate.offer.discount_name,
          type: 'fixed',
          value: formatCurrency(discount_value),
          rawValue: discount_value,
          amount: discount_amount,
          discountId: id // Include the discount ID
        };
      }
    }
    
    if (guestPricing.hasDiscount && guestPricing.originalPrice > 0) {
      const discount = ((guestPricing.originalPrice - guestPricing.discountedPrice) / guestPricing.originalPrice) * 100;
      if (discount > 0) {
        return {
          name: rate.offer?.discount_name,
          type: 'percentage',
          value: `${Math.round(discount)}%`,
          rawValue: Math.round(discount),
          discountId: rate.offer?.id || null // Include the discount ID
        };
      }
    }
    
    return null;
  };

  const discountInfo = getDiscountInfo();

  const calculateSavings = () => {
  // Use the per-room savings calculation
  return guestPricing.originalPrice - guestPricing.discountedPrice;
};

  const savingsAmount = calculateSavings();

  // --- NEW: Special calculation for free night offers ---
// --- NEW: Correct free night calculation ---
// const calculateFreeNightDisplayPrice = () => {
//   const nights = calculateNights();
//   const freeNights = rate.offer?.free_nights || 0;
//   const minStayNights = rate.offer?.min_stay_nights || 1;
  
//   console.log(`Free night calculation: nights=${nights}, freeNights=${freeNights}, minStay=${minStayNights}`);
  
//   if (!rate.offer || rate.offer.discount_form !== 'free_nights' || nights < minStayNights) {
//     return calculateDisplayPriceData();
//   }
  
//   const roomsInCart = getCartItemsForThisRate();
//   const roomsNeeded = state.searchState.room_guest.length;
//   const roomsToCalculate = roomsInCart.length > 0 ? roomsInCart.length : roomsNeeded;
  
//   let totalPriceForStay = 0;
//   let totalOriginalPriceForStay = 0;
  
//   console.log(`Calculating for ${roomsToCalculate} rooms needed`);
  
//   for (let i = 0; i < roomsToCalculate; i++) {
//     let guestConfig;
    
//     if (roomsInCart.length > 0) {
//       const cartItem = roomsInCart[i];
//       guestConfig = state.searchState.room_guest[cartItem.roomNumber - 1];
//     } else {
//       guestConfig = state.searchState.room_guest[i];
//     }
    
//     if (!guestConfig) continue;
    
//     const adults = guestConfig.no_of_adults;
//     const children = guestConfig.no_of_children;
//     const childAges = guestConfig.child_ages || [];
    
//     // **CRITICAL: Pass true for isFreeNightCalculation parameter**
//     const pricing = getPricingForGuests(adults, children, childAges, true);
    
//     const originalPricePerNight = pricing.originalPrice;
//     const discountedPricePerNight = pricing.discountedPrice;
    
//     // For EACH NIGHT:
//     // - For payable nights: Use ORIGINAL price
//     // - For free night: Use DISCOUNTED price
//     const payableNights = Math.max(nights - freeNights, 0);
    
//     // Price for payable nights (use ORIGINAL price)
//     const priceForPayableNights = originalPricePerNight * payableNights;
    
//     // Price for free night (use DISCOUNTED price)
//     const priceForFreeNights = discountedPricePerNight * freeNights;
    
//     const totalForStay = priceForPayableNights + priceForFreeNights;
//     const originalTotalForStay = originalPricePerNight * nights;
    
//     totalPriceForStay += totalForStay;
//     totalOriginalPriceForStay += originalTotalForStay;
    
//     console.log(`Room ${i+1}: ${adults}A ${children}C`);
//     console.log(`  Original per night: ${originalPricePerNight}`);
//     console.log(`  Discounted per night: ${discountedPricePerNight}`);
//     console.log(`  Payable nights (${payableNights}) × Original: ${originalPricePerNight} × ${payableNights} = ${priceForPayableNights}`);
//     console.log(`  Free nights (${freeNights}) × Discounted: ${discountedPricePerNight} × ${freeNights} = ${priceForFreeNights}`);
//     console.log(`  Room total for stay: ${totalForStay}`);
//   }
  
//   // Calculate average PER NIGHT for display
//   const averagePricePerNight = totalPriceForStay / nights;
//   const averageOriginalPricePerNight = totalOriginalPriceForStay / nights;
  
//   console.log(`Final totals:`);
//   console.log(`  Total for stay: ${totalPriceForStay}`);
//   console.log(`  Average per night: ${averagePricePerNight}`);
//   console.log(`  Display price: ${Math.round(averagePricePerNight)}`);
  
//   return {
//     totalPrice: Math.round(averagePricePerNight),
//     totalOriginalPrice: Math.round(averageOriginalPricePerNight),
//     roomCount: roomsToCalculate,
//     isExact: roomsInCart.length > 0,
//     hasDiscount: averageOriginalPricePerNight > averagePricePerNight,
//     isFreeNightOffer: true,
//     nights: nights,
//     freeNights: freeNights,
//     payableNights: Math.max(nights - freeNights, 0),
//     totalForStay: totalPriceForStay,
//     originalTotalForStay: totalOriginalPriceForStay
//   };
// };

const calculateFreeNightDisplayPrice = () => {
  const nights = calculateNights();
  const freeNights = rate.offer?.free_nights || 0;
  const minStayNights = rate.offer?.min_stay_nights || 1;
  
  if (!rate.offer || rate.offer.discount_form !== 'free_nights' || nights < minStayNights) {
    return calculateDisplayPriceData();
  }
  
  const roomsInCart = getCartItemsForThisRate();
  const roomsNeeded = state.searchState.room_guest.length;
  const roomsToCalculate = roomsInCart.length > 0 ? roomsInCart.length : roomsNeeded;
  
  let totalPriceForStay = 0;
  let totalOriginalPriceForStay = 0;
  
  for (let i = 0; i < roomsToCalculate; i++) {
    let guestConfig;
    
    if (roomsInCart.length > 0) {
      const cartItem = roomsInCart[i];
      guestConfig = state.searchState.room_guest[cartItem.roomNumber - 1];
    } else {
      guestConfig = state.searchState.room_guest[i];
    }
    
    if (!guestConfig) continue;
    
    // SIMPLE: Use the rate's price directly from API
    // rate.price is already the AVERAGE per night after ALL discounts
    // rate.originalPrice is the base price before ANY discounts
    
    const averagePricePerNight = rate.price; // This is already calculated correctly by backend
    const originalBasePrice = rate.originalPrice;
    
    // Calculate total for the stay using the average price per night
    const totalForRoom = averagePricePerNight * nights;
    const originalTotalForRoom = originalBasePrice * nights;
    
    totalPriceForStay += totalForRoom;
    totalOriginalPriceForStay += originalTotalForRoom;
  }
  
  // Average PER NIGHT for display
  const averagePricePerNight = totalPriceForStay / nights;
  const averageOriginalPricePerNight = totalOriginalPriceForStay / nights;
  
  return {
    totalPrice: Math.round(averagePricePerNight),
    totalOriginalPrice: Math.round(averageOriginalPricePerNight),
    roomCount: roomsToCalculate,
    isExact: roomsInCart.length > 0,
    hasDiscount: averageOriginalPricePerNight > averagePricePerNight,
    isFreeNightOffer: true,
    nights: nights,
    freeNights: freeNights,
    payableNights: Math.max(nights - freeNights, 0),
    totalForStay: totalPriceForStay,
    originalTotalForStay: totalOriginalPriceForStay
  };
};
  // --- Calculate display price: Show price for ALL required rooms initially, then selected rooms after selection ---
const calculateDisplayPriceData = () => {
  // Check if this is a free night offer
  const isFreeNightOffer = rate.offer && rate.offer.discount_form === 'free_nights';
  const nights = calculateNights();
  
  // console.log(`Display price calculation: isFreeNightOffer=${isFreeNightOffer}, nights=${nights}, offer=${rate.offer?.discount_form}`);
  
  if (isFreeNightOffer && nights >= (rate.offer?.min_stay_nights || 1)) {
    // console.log('Using free night calculation');
    return calculateFreeNightDisplayPrice();
  }
  
  // Regular calculation for non-free-night offers
  const roomsInCart = getCartItemsForThisRate();
  
  if (roomsInCart.length > 0) {
    // When rooms are selected, show price for selected rooms only
    let totalPrice = 0;
    let totalOriginalPrice = 0;
    
    roomsInCart.forEach(item => {
      totalPrice += item.roomPrice;
      totalOriginalPrice += item.originalPrice || item.roomPrice;
    });
    
    // console.log(`Regular offer with rooms selected: TotalPrice=${totalPrice}, TotalOriginal=${totalOriginalPrice}, Rooms=${roomsInCart.length}`);
    
    return {
      totalPrice,
      totalOriginalPrice,
      roomCount: roomsInCart.length,
      isExact: true,
      hasDiscount: totalOriginalPrice > totalPrice
    };
  } else {
    // When no rooms selected, show price for ALL required rooms (even if not all available)
    const roomsNeeded = state.searchState.room_guest.length;
    let totalPrice = 0;
    let totalOriginalPrice = 0;
    let hasAnyDiscount = false;
    
    for (let i = 0; i < roomsNeeded; i++) {
      const guestConfig = state.searchState.room_guest[i];
      const pricing = getPricingForGuests(
        guestConfig.no_of_adults, 
        guestConfig.no_of_children, 
        guestConfig.child_ages || []
      );
      totalPrice += pricing.discountedPrice;
      totalOriginalPrice += pricing.originalPrice;
      if (pricing.hasDiscount) hasAnyDiscount = true;
    }
    
    // console.log(`Regular offer no rooms selected: TotalPrice=${totalPrice}, TotalOriginal=${totalOriginalPrice}, Rooms=${roomsNeeded}, HasDiscount=${hasAnyDiscount}`);
    
    return {
      totalPrice,
      totalOriginalPrice,
      roomCount: roomsNeeded,
      isExact: false,
      hasDiscount: hasAnyDiscount
    };
  }
};
  const displayPriceData = calculateDisplayPriceData();

  // --- handleRateAction ---
  // const handleRateAction = async (action) => {
  //   const { checkin, checkout } = state.searchState;
  //   if (!checkin || !checkout || checkout <= checkin) {
  //     alert("Please select valid Check-in and Check-out dates first.");
  //     return;
  //   }

  //   const sessionData = { ...state };
  //   const nights = calculateNights();

  //   if (action === 'select') {
  //     if (allRequiredRoomsSelected) {
  //       alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
  //       return;
  //     }

  //     const roomsNeeded = state.searchState.room_guest.length;
  //     const roomsAlreadySelected = currentRoomCountForThisCategory;
  //     const roomsToAdd = Math.min(
  //       roomsNeeded,
  //       totalRoomsAvailable - roomsAlreadySelected,
  //       roomsStillNeeded
  //     );

  //     if (roomsToAdd <= 0) {
  //       if (roomsAlreadySelected >= totalRoomsAvailable) {
  //         alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
  //       } else if (allRequiredRoomsSelected) {
  //         alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
  //       }
  //       return;
  //     }

  //     for (let i = 0; i < roomsToAdd; i++) {
  //       const roomIndex = roomsAlreadySelected + i;
  //       const guestConfig = state.searchState.room_guest[roomIndex];
        
  //       if (!guestConfig) {
  //         console.error(`No guest config found for room ${roomIndex + 1}`);
  //         continue;
  //       }

  //       const roomPrice = getPricingForGuests(
  //         guestConfig.no_of_adults, 
  //         guestConfig.no_of_children,
  //         guestConfig.child_ages || []
  //       );
  //       const basePrice = rate.base_price || rate.originalPrice || roomPrice;
        
  //       const newCartItem = {
  //         id: rate.id,
  //         roomId: room.id,
  //         roomNumber: roomIndex + 1,
  //         roomName: room.name,
  //         rateName: rate.name,
  //         roomPrice: roomPrice,
  //         basePrice: basePrice,
  //         adultsCount: Math.min(guestConfig.no_of_adults, room.maxGuests || 3),
  //         childrenCount: Math.min(guestConfig.no_of_children, 2),
  //         imageUrl: room.images[0],
  //         discountInfo: discountInfo ? {
  //           name: discountInfo.name,
  //           type: discountInfo.type,
  //           value: discountInfo.value,
  //           rawValue: discountInfo.rawValue
  //         } : null,
  //         offerName: discountInfo ? discountInfo.name : null,
  //         offerFreeNights: rate.offerFreeNights || 0,
  //         quantity: 1,
  //         guestInfo: {
  //           adults: guestConfig.no_of_adults,
  //           children: guestConfig.no_of_children
  //         },
  //         originalGuestConfig: {
  //           adults: guestConfig.no_of_adults,
  //           children: guestConfig.no_of_children,
  //           roomNo: roomIndex + 1
  //         },
  //         savings: savingsAmount,
  //         guestDisplay: `${guestConfig.no_of_adults} Adult${guestConfig.no_of_adults > 1 ? 's' : ''}${guestConfig.no_of_children > 0 ? `, ${guestConfig.no_of_children} Child${guestConfig.no_of_children > 1 ? 'ren' : ''}` : ''}`,
  //           ratePlan: {
  //                 id: rate.id,
  //                 name: rate.name,
  //                 price: rate.price,
  //                 offer: rate.offer // This contains discount_form and no_free_nights
  //               }
  //       };
        
  //       sessionData.cart.push(newCartItem);
  //     }

  //   } else if (action === 'plus') {
  //     if (allRequiredRoomsSelected) {
  //       alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
  //       return;
  //     }

  //     if (isCategoryLimitReached) {
  //       alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
  //       return;
  //     }

  //     const nextRoomIndex = currentRoomCountForThisRate;
  //     const guestConfig = state.searchState.room_guest[nextRoomIndex];
      
  //     if (!guestConfig) {
  //       alert(`No guest configuration found for room ${nextRoomIndex + 1}`);
  //       return;
  //     }

  //     const roomPrice = getPricingForGuests(guestConfig.no_of_adults, guestConfig.no_of_children, guestConfig.child_ages || []);
  //     const basePrice = rate.base_price || rate.originalPrice || roomPrice;
      
  //     const newCartItem = {
  //       id: rate.id,
  //       roomId: room.id,
  //       roomNumber: nextRoomIndex + 1,
  //       roomName: room.name,
  //       rateName: rate.name,
  //       roomPrice: roomPrice,
  //       basePrice: basePrice,
  //       adultsCount: Math.min(guestConfig.no_of_adults, room.maxGuests || 3),
  //       childrenCount: Math.min(guestConfig.no_of_children, 2),
  //       imageUrl: room.images[0],
  //       discountInfo: discountInfo ? {
  //         name: discountInfo.name,
  //         type: discountInfo.type,
  //         value: discountInfo.value,
  //         rawValue: discountInfo.rawValue
  //       } : null,
  //       offerName: discountInfo ? discountInfo.name : null,
  //       offerFreeNights: rate.offerFreeNights || 0,
  //       quantity: 1,
  //       guestInfo: {
  //         adults: guestConfig.no_of_adults,
  //         children: guestConfig.no_of_children
  //       },
  //       originalGuestConfig: {
  //         adults: guestConfig.no_of_adults,
  //         children: guestConfig.no_of_children,
  //         roomNo: nextRoomIndex + 1
  //       },
  //       savings: savingsAmount,
  //       guestDisplay: `${guestConfig.no_of_adults} Adult${guestConfig.no_of_adults > 1 ? 's' : ''}${guestConfig.no_of_children > 0 ? `, ${guestConfig.no_of_children} Child${guestConfig.no_of_children > 1 ? 'ren' : ''}` : ''}`,
  //       ratePlan: {
  //         id: rate.id,
  //         name: rate.name,
  //         price: rate.price,
  //         offer: rate.offer // This contains discount_form and no_free_nights
  //       }
  //     };
  //     sessionData.cart.push(newCartItem);

  //   } else if (action === 'minus') {
  //     const rateItems = getCartItemsForThisRate();
      
  //     if (rateItems.length > 0) {
  //       const lastAddedItem = rateItems.reduce((prev, current) => 
  //         (prev.roomNumber > current.roomNumber) ? prev : current
  //       );
        
  //       const indexToRemove = sessionData.cart.findIndex(item => 
  //         item.id === lastAddedItem.id && 
  //         item.roomId === lastAddedItem.roomId && 
  //         item.roomNumber === lastAddedItem.roomNumber
  //       );
        
  //       if (indexToRemove > -1) {
  //         sessionData.cart.splice(indexToRemove, 1);
  //       }
  //     }
  //   }

  //   dispatch({ type: 'UPDATE_CART', payload: sessionData.cart });
    
  //   // Recalculate taxes
  //   const calculateTaxesForCart = async () => {
  //     try {
  //       const formatDate = (date) => {
  //         if (date instanceof Date) {
  //           return date.toISOString().split('T')[0];
  //         }
  //         return new Date(date).toISOString().split('T')[0];
  //       };

  //       const nights = calculateNights();
        
  //       const taxPromises = [];
        
  //       sessionData.cart.forEach((item) => {
  //         const perNightPrice = item.roomPrice;
          
  //         const payload = {
  //           type: "tariff",
  //           amount: perNightPrice,
  //           check_in_date: formatDate(checkin),
  //           check_out_date: formatDate(checkout),
  //           id_shop: SHOP_ID 
  //         };

  //         taxPromises.push(
  //           fetch(`${API_BASE_URL}/fo/mstCalculation`, {
  //             method: 'POST',
  //             headers: {
  //               'Accept': 'application/json',
  //               'Content-Type': 'application/json',
  //             },
  //             body: JSON.stringify(payload)
  //           })
  //           .then(response => response.json())
  //           .then(data => {
  //             if (data.total_tax) {
  //               const totalTax = data.total_tax;
  //               return {
  //                 roomName: item.roomName,
  //                 perNightPrice: perNightPrice,
  //                 taxData: {
  //                   base_amount_total: totalTax.base_amount_total || 0,
  //                   total_cgst_amount_after_tax: totalTax.total_cgst_amount_after_tax || 0,
  //                   total_sgst_amount_after_tax: totalTax.total_sgst_amount_after_tax || 0,
  //                   total_cess_amount: totalTax.total_cess_amount || 0,
  //                   grand_total: totalTax.grand_total || 0
  //                 }
  //               };
  //             }
  //             return null;
  //           })
  //         );
  //       });

  //       const responses = await Promise.all(taxPromises);
  //       const validResponses = responses.filter(r => r !== null);
        
  //       const cumulativeTotals = {
  //         base_amount_total: 0,
  //         total_cgst_amount_after_tax: 0,
  //         total_sgst_amount_after_tax: 0,
  //         total_cess_amount: 0,
  //         grand_total: 0,
  //         perNightBaseTotal: 0
  //       };
        
  //       validResponses.forEach((roomData) => {
  //         cumulativeTotals.base_amount_total += roomData.taxData.base_amount_total;
  //         cumulativeTotals.total_cgst_amount_after_tax += roomData.taxData.total_cgst_amount_after_tax;
  //         cumulativeTotals.total_sgst_amount_after_tax += roomData.taxData.total_sgst_amount_after_tax;
  //         cumulativeTotals.total_cess_amount += roomData.taxData.total_cess_amount;
  //         cumulativeTotals.grand_total += roomData.taxData.grand_total;
  //         cumulativeTotals.perNightBaseTotal += roomData.perNightPrice;
  //       });
        
  //       const rateTotal = sessionData.cart.reduce((sum, item) => 
  //         sum + (item.roomPrice * nights), 0
  //       );
        
  //       const totalTaxes = cumulativeTotals.total_cgst_amount_after_tax + 
  //                         cumulativeTotals.total_sgst_amount_after_tax + 
  //                         cumulativeTotals.total_cess_amount;
        
  //       dispatch({
  //         type: 'UPDATE_TOTALS',
  //         payload: {
  //           rateTotal: rateTotal,
  //           subtotal: cumulativeTotals.base_amount_total,
  //           taxes: totalTaxes,
  //           total: cumulativeTotals.grand_total,
  //           finalTotal: cumulativeTotals.grand_total,
  //           taxBreakdown: {
  //             cgst: cumulativeTotals.total_cgst_amount_after_tax,
  //             sgst: cumulativeTotals.total_sgst_amount_after_tax,
  //             cess: cumulativeTotals.total_cess_amount,
  //             totalTaxPercentage: totalTaxes > 0 ? ((totalTaxes / cumulativeTotals.base_amount_total) * 100).toFixed(2) : '0.00',
  //             nights: nights,
  //             perNightBaseTotal: cumulativeTotals.perNightBaseTotal
  //           }
  //         }
  //       });

  //     } catch (error) {
  //       console.error('Tax calculation error:', error);
        
  //       const nights = calculateNights();
  //       const totalRoomRate = sessionData.cart.reduce((sum, item) => 
  //         sum + (item.roomPrice * nights), 0
  //       );
  //       const rateTotal = totalRoomRate;
  //       const subtotal = rateTotal - (state.offerDiscount || 0);
  //       const taxes = subtotal * 0.18;
  //       const total = subtotal + taxes;
        
  //       dispatch({
  //         type: 'UPDATE_TOTALS',
  //         payload: {
  //           rateTotal,
  //           subtotal,
  //           taxes,
  //           total,
  //           finalTotal: total,
  //           taxBreakdown: null
  //         }
  //       });
  //     }
  //   };
    
  //   await calculateTaxesForCart();
  // };

// const handleRateAction = async (action) => {
//   const { checkin, checkout } = state.searchState;
//   if (!checkin || !checkout || checkout <= checkin) {
//     alert("Please select valid Check-in and Check-out dates first.");
//     return;
//   }

//   const sessionData = { ...state };
//   const nights = calculateNights();
//   const isFreeNightOffer = rate.offer && rate.offer.discount_form === 'free_nights';
//   const freeNights = rate.offer?.no_free_nights || 0;
//   const minStayNights = rate.offer?.min_stay_nights || 1;

//   // Get room capacity constraints
//   const maxAdultsAllowed = room.no_adults_allowed || 2;
//   const maxChildrenAllowed = room.no_childs_allowed || 2;
//   const maxGuests = room.maxGuests || (maxAdultsAllowed + maxChildrenAllowed);
// console.log("Selected MaxChild",maxChildrenAllowed);
//   // UPDATED: Modified tax calculation function to handle free nights
//   const calculateTaxForRoom = async (roomInfo) => {
//     try {
//       const formatDate = (date) => {
//         if (date instanceof Date) {
//           return date.toISOString().split('T')[0];
//         }
//         return new Date(date).toISOString().split('T')[0];
//       };

//       // CRITICAL FIX: Calculate AVERAGE PER NIGHT amount for free night offers
//       let amountForTaxCalculation;
//       let isFreeNight = false;
      
//       if (roomInfo.isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
//         // For free night offers: calculate AVERAGE per night amount
//         const payableNights = Math.max(nights - freeNights, 0);
//         const priceForPayableNights = roomInfo.originalPrice * payableNights;
//         const priceForFreeNights = roomInfo.roomPrice * freeNights;
//         const totalForStay = priceForPayableNights + priceForFreeNights;
//         amountForTaxCalculation = totalForStay / nights; // AVERAGE per night
//         isFreeNight = true;
        
//         console.log(`Free night tax calculation for ${roomInfo.roomName}:`);
//         console.log(`  Original per night: ${roomInfo.originalPrice}`);
//         console.log(`  Discounted per night: ${roomInfo.roomPrice}`);
//         console.log(`  Nights: ${nights}, Free: ${freeNights}, Payable: ${payableNights}`);
//         console.log(`  Total for stay: ${totalForStay}`);
//         console.log(`  Average per night for tax: ${amountForTaxCalculation}`);
//       } else {
//         // Regular tax calculation: use per-night price
//         amountForTaxCalculation = roomInfo.roomPrice;
//       }

//       const payload = {
//         type: "tariff",
//         amount: amountForTaxCalculation,
//         check_in_date: formatDate(checkin),
//         check_out_date: formatDate(checkout),
//         id_shop: SHOP_ID 
//       };

//       console.log(`Tax calculation payload:`, payload);

//       const response = await fetch(`${API_BASE_URL}/fo/mstCalculation`, {
//         method: 'POST',
//         headers: {
//           'Accept': 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(payload)
//       });

//       const data = await response.json();
      
//       if (data.total_tax) {
//         const totalTax = data.total_tax;
        
//         // The API returns total tax for all nights
//         // For free nights, we need to adjust the tax per night
//         let per_night_tax = 0;
//         let per_night_price = roomInfo.roomPrice;
        
//         if (isFreeNight) {
//           // For free nights: calculate actual average tax per night
//           const totalTaxAmount = (totalTax.total_cgst_amount_after_tax || 0) + 
//                                 (totalTax.total_sgst_amount_after_tax || 0) + 
//                                 (totalTax.total_cess_amount || 0);
//           per_night_tax = totalTaxAmount / nights;
//           per_night_price = amountForTaxCalculation; // Use average price
//         } else {
//           per_night_tax = (totalTax.grand_total - totalTax.base_amount_total) || 0;
//         }
        
//         return {
//           roomName: roomInfo.roomName || 'Room',
//           roomPrice: roomInfo.roomPrice,
//           originalPrice: roomInfo.originalPrice,
//           isFreeNightOffer: isFreeNight,
//           taxData: {
//             base_amount_total: totalTax.base_amount_total || 0,
//             total_cgst_amount_after_tax: totalTax.total_cgst_amount_after_tax || 0,
//             total_sgst_amount_after_tax: totalTax.total_sgst_amount_after_tax || 0,
//             total_cess_amount: totalTax.total_cess_amount || 0,
//             grand_total: totalTax.grand_total || 0,
//             per_night_tax: per_night_tax,
//             per_night_price: per_night_price,
//             daily_breakdown: data.daily_breakdown || []
//           }
//         };
//       }
//       return null;
//     } catch (error) {
//       console.error('Tax calculation error for single room:', error);
//       return null;
//     }
//   };

//   // Calculate tax for each new room
//   const taxPromises = [];
//   const newRoomsInfo = [];

//   if (action === 'select' || action === 'plus') {
//     // Determine how many rooms to add
//     let roomsToAdd = 0;
//     let startIndex = 0;
    
//     if (action === 'select') {
//       if (allRequiredRoomsSelected) {
//         alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
//         return;
//       }

//       const roomsNeeded = state.searchState.room_guest.length;
//       const roomsAlreadySelected = getCurrentRoomCountForThisCategory();
      
//       roomsToAdd = Math.min(
//         roomsNeeded,
//         totalRoomsAvailable - roomsAlreadySelected,
//         roomsStillNeeded
//       );
      
//       startIndex = roomsAlreadySelected;

//     if (roomsToAdd <= 0) {
//       if (roomsAlreadySelected >= totalRoomsAvailable) {
//         alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
//       } else if (allRequiredRoomsSelected) {
//         alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
//       }
//       return;
//     }

//     } else { // action === 'plus'
//       if (allRequiredRoomsSelected) {
//         alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
//         return;
//       }

//       if (isCategoryLimitReached) {
//         alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
//         return;
//       }

//       roomsToAdd = 1;
//       startIndex = getCurrentRoomCountForThisRate();
//     }

//     // CRITICAL FIX: Get ALL cart items to determine which room numbers are already taken
//     const allCartItems = state.cart;
//     const takenRoomNumbers = new Set(allCartItems.map(item => item.roomNumber));
    
//     // Find available room numbers
//     let availableRoomNumbers = [];
//     for (let i = 0; i < state.searchState.room_guest.length; i++) {
//       const roomNum = i + 1;
//       if (!takenRoomNumbers.has(roomNum)) {
//         availableRoomNumbers.push(roomNum);
//       }
//     }
    
//     // Sort ascending to assign lowest available numbers first
//     availableRoomNumbers.sort((a, b) => a - b);
    
//     // Take the first 'roomsToAdd' available room numbers
//     const roomNumbersToAssign = availableRoomNumbers.slice(0, roomsToAdd);

//  // --- CAPACITY VALIDATION: Check each room's guest count against capacity BEFORE adding ---
// // Get room capacity constraints
// const maxAdultsAllowed = Number(room.no_adults_allowed) || 0;
// const maxChildrenAllowed = Number(room.no_childs_allowed) || 0;
// const maxExtraBedsAllowed = Number(room.no_extrabeds_allowed) || 0;

// // Calculate maximum guests with the understanding that extra beds can be used by EITHER adults OR children
// const maxTotalAdults = maxAdultsAllowed + maxExtraBedsAllowed; // Maximum adults possible with extra beds
// const maxTotalChildren = maxChildrenAllowed + maxExtraBedsAllowed; // Maximum children possible with extra beds
// const maxTotalGuests = maxAdultsAllowed + maxChildrenAllowed + maxExtraBedsAllowed; // Total guests possible

// console.log('🔍 CAPACITY DEBUG:', {
//   roomName: room.category_name || room.name,
//   no_adults_allowed: room.no_adults_allowed,
//   no_childs_allowed: room.no_childs_allowed,
//   no_extrabeds_allowed: room.no_extrabeds_allowed,
//   maxAdultsAllowed,
//   maxChildrenAllowed,
//   maxExtraBedsAllowed,
//   maxTotalAdults: `${maxAdultsAllowed} base + up to ${maxExtraBedsAllowed} extra = ${maxTotalAdults}`,
//   maxTotalChildren: `${maxChildrenAllowed} base + up to ${maxExtraBedsAllowed} extra = ${maxTotalChildren}`,
//   maxTotalGuests: `${maxAdultsAllowed} adults + ${maxChildrenAllowed} children + ${maxExtraBedsAllowed} extra beds = ${maxTotalGuests}`
// });

// // Track validation issues
// let validationErrors = [];

// for (let i = 0; i < roomNumbersToAssign.length; i++) {
//   const roomNumber = roomNumbersToAssign[i];
//   const roomIndex = roomNumber - 1;
  
//   const guestConfig = state.searchState.room_guest[roomIndex];
  
//   if (!guestConfig) {
//     console.error(`No guest config found for room ${roomNumber}`);
//     continue;
//   }

//   const selectedAdults = guestConfig.no_of_adults;
//   const selectedChildren = guestConfig.no_of_children;
//   const totalSelectedGuests = selectedAdults + selectedChildren;
  
//   console.log(`📊 Room ${roomNumber} Selection:`, {
//     selectedAdults,
//     selectedChildren,
//     totalSelectedGuests,
//     maxAdultsAllowed,
//     maxChildrenAllowed,
//     maxExtraBedsAllowed,
//     maxTotalAdults,
//     maxTotalChildren,
//     maxTotalGuests
//   });

//   // IMPROVED VALIDATION LOGIC:
//   // 1. First, check if total guests exceeds absolute maximum
//   if (totalSelectedGuests > maxTotalGuests) {
//     validationErrors.push({
//       roomNumber,
//       adults: selectedAdults,
//       children: selectedChildren,
//       total: totalSelectedGuests,
//       maxTotalGuests,
//       errorType: 'totalGuests',
//       reason: `Total guests (${totalSelectedGuests}) exceeds maximum allowed (${maxTotalGuests})`
//     });
//     continue;
//   }
  
//   // 2. Check if adults exceed maximum possible adults
//   if (selectedAdults > maxTotalAdults) {
//     validationErrors.push({
//       roomNumber,
//       adults: selectedAdults,
//       children: selectedChildren,
//       maxTotalAdults,
//       errorType: 'adults',
//       reason: `Adults (${selectedAdults}) exceeds maximum possible adults including extra beds (${maxTotalAdults})`
//     });
//     continue;
//   }
  
//   // 3. Check if children exceed maximum possible children
//   if (selectedChildren > maxTotalChildren) {
//     validationErrors.push({
//       roomNumber,
//       adults: selectedAdults,
//       children: selectedChildren,
//       maxTotalChildren,
//       errorType: 'children',
//       reason: `Children (${selectedChildren}) exceeds maximum possible children including extra beds (${maxTotalChildren})`
//     });
//     continue;
//   }
  
//   // 4. Calculate if we have enough extra beds for the combined needs
//   // Calculate how many extra beds are needed for adults beyond base
//   const extraAdultsNeeded = Math.max(0, selectedAdults - maxAdultsAllowed);
  
//   // Calculate how many extra beds are needed for children beyond base
//   const extraChildrenNeeded = Math.max(0, selectedChildren - maxChildrenAllowed);
  
//   // Total extra beds needed (can be shared between adults and children)
//   const totalExtraBedsNeeded = extraAdultsNeeded + extraChildrenNeeded;
  
//   // Check if we have enough extra beds
//   if (totalExtraBedsNeeded > maxExtraBedsAllowed) {
//     validationErrors.push({
//       roomNumber,
//       adults: selectedAdults,
//       children: selectedChildren,
//       extraAdultsNeeded,
//       extraChildrenNeeded,
//       totalExtraBedsNeeded,
//       maxExtraBedsAllowed,
//       errorType: 'extraBeds',
//       reason: `Need ${totalExtraBedsNeeded} extra beds but only ${maxExtraBedsAllowed} available`
//     });
//     continue;
//   }
  
//   console.log(`✅ Room ${roomNumber} capacity validation passed`);
// }

// // If there are validation errors, show a comprehensive alert
// if (validationErrors.length > 0) {
//   const error = validationErrors[0]; // Get the first error
  
//   // Create user-friendly error message
//   let errorMessage = '';
  
//   switch (error.errorType) {
//     case 'totalGuests':
//       errorMessage = `Room can accommodate maximum ${maxTotalGuests} total guests (including extra beds).`;
//       break;
//     case 'adults':
//       errorMessage = `Maximum ${maxTotalAdults} adults allowed in this room (including extra beds).`;
//       break;
//     case 'children':
//       errorMessage = `Maximum ${maxTotalChildren} children allowed in this room (including extra beds).`;
//       break;
//     case 'extraBeds':
//       errorMessage = `Need ${error.totalExtraBedsNeeded} extra bed(s) but only ${maxExtraBedsAllowed} available.`;
//       break;
//     default:
//       errorMessage = 'Guest configuration exceeds room capacity.';
//   }
  
//   // Call the parent's function
//   onCapacityError({
//     roomName: room.category_name || room.name,
//     roomNumber: error.roomNumber,
//     adults: error.adults,
//     children: error.children,
//     maxAdultsAllowed,
//     maxChildrenAllowed,
//     maxExtraBedsAllowed,
//     baseAdults: maxAdultsAllowed,
//     baseChildren: maxChildrenAllowed,
//     extraBeds: maxExtraBedsAllowed,
//     totalAdultsAllowed: maxTotalAdults,
//     totalChildrenAllowed: maxTotalChildren,
//     totalGuestsAllowed: maxTotalGuests,
//     extraAdultsNeeded: error.extraAdultsNeeded || 0,
//     extraChildrenNeeded: error.extraChildrenNeeded || 0,
//     totalExtraBedsNeeded: error.totalExtraBedsNeeded || 0,
//     errorType: error.errorType,
//     errorMessage
//   });
  
//   return; // Stop the entire operation
// }

// console.log(`✅ Capacity validation passed for all ${roomNumbersToAssign.length} rooms`);


//     // If validation passed, proceed with adding rooms
//     for (let i = 0; i < roomNumbersToAssign.length; i++) {
//       const roomNumber = roomNumbersToAssign[i];
//       const roomIndex = roomNumber - 1; // Convert to 0-based index
      
//       // Get guest config directly by room number
//       const guestConfig = state.searchState.room_guest[roomIndex];
      
//       if (!guestConfig) {
//         console.error(`No guest config found for room ${roomNumber}`);
//         continue;
//       }

//       console.log(`Assigning room ${roomNumber} with guest config:`, guestConfig);

//       // Use getPricingForGuests which now returns both original and discounted prices
//       const pricing = getPricingForGuests(
//         guestConfig.no_of_adults, 
//         guestConfig.no_of_children,
//         guestConfig.child_ages || []
//       );
      
//       // Store room info for later
//       const roomInfo = {
//         roomNumber: roomNumber,
//         roomIndex: roomIndex,
//         guestConfig,
//         roomPrice: pricing.discountedPrice,
//         originalPrice: pricing.originalPrice,
//         roomId: room.id,
//         roomName: room.name,
//         rateId: rate.id,
//         hasDiscount: pricing.hasDiscount,
//         isFreeNightOffer: isFreeNightOffer && nights >= minStayNights && freeNights > 0
//       };
      
//       newRoomsInfo.push(roomInfo);
      
//       // Add to tax promises - pass the complete room info
//       taxPromises.push(calculateTaxForRoom(roomInfo));
//     }

//   } else if (action === 'minus') {
//     const rateItems = getCartItemsForThisRate();
    
//     if (rateItems.length > 0) {
//       // Find the highest room number for this rate to remove
//       const highestRoomItem = rateItems.reduce((prev, current) => 
//         (prev.roomNumber > current.roomNumber) ? prev : current
//       );
      
//       const indexToRemove = sessionData.cart.findIndex(item => 
//         item.id === highestRoomItem.id && 
//         item.roomId === highestRoomItem.roomId && 
//         item.roomNumber === highestRoomItem.roomNumber
//       );
      
//       if (indexToRemove > -1) {
//         sessionData.cart.splice(indexToRemove, 1);
//       }
//     }
//   }

//   // If we're adding rooms (select or plus), wait for tax calculations
//   if ((action === 'select' || action === 'plus') && taxPromises.length > 0) {
//     try {
//       // Wait for all tax calculations
//       const taxResults = await Promise.all(taxPromises);
      
//       // Now create cart items with tax data
//       newRoomsInfo.forEach((roomInfo, index) => {
//         const { roomNumber, guestConfig, roomPrice, originalPrice, roomId, roomName, rateId, hasDiscount, isFreeNightOffer } = roomInfo;
//         const basePrice = rate.base_price || rate.originalPrice || roomPrice;
//         const taxData = taxResults[index];
        
//         // Get discount ID from rate.offer
//         const discountId = rate.offer?.id || null;
        
//         // Calculate total for stay based on free night logic
//         let totalForStay = 0;
//         let originalTotalForStay = 0;
//         let averagePricePerNight = roomPrice;
        
//         if (isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
//           const payableNights = Math.max(nights - freeNights, 0);
//           const priceForPayableNights = originalPrice * payableNights;
//           const priceForFreeNights = roomPrice * freeNights;
//           totalForStay = priceForPayableNights + priceForFreeNights;
//           originalTotalForStay = originalPrice * nights;
//           averagePricePerNight = totalForStay / nights;
//         } else {
//           totalForStay = roomPrice * nights;
//           originalTotalForStay = originalPrice * nights;
//           averagePricePerNight = roomPrice;
//         }
        
//         const newCartItem = {
//           id: rate.id,
//           roomId: roomId,
//           roomNumber: roomNumber, // Use the actual room number
//           roomName: roomName,
//           rateName: rate.name,
//           roomPrice: roomPrice, // This is the discounted per-night price
//           originalPrice: originalPrice, // This is the original per-night price
//           averagePricePerNight: averagePricePerNight,
//           totalForStay: totalForStay,
//           originalTotalForStay: originalTotalForStay,
//           basePrice: basePrice,
//           adultsCount: guestConfig.no_of_adults,
//           childrenCount: guestConfig.no_of_children,
//           childAges: guestConfig.child_ages || [], // Store child ages
//           imageUrl: room.images[0],
//           discountInfo: discountInfo ? {
//             name: discountInfo.name,
//             type: discountInfo.type,
//             value: discountInfo.value,
//             rawValue: discountInfo.rawValue,
//             discountId: discountId
//           } : null,
//           offerName: discountInfo ? discountInfo.name : (rate.offer?.discount_name || null),
//           offerFreeNights: rate.offerFreeNights || 0,
//           quantity: 1,
//           guestInfo: {
//             adults: guestConfig.no_of_adults,
//             children: guestConfig.no_of_children,
//             childAges: guestConfig.child_ages || [], 
//           },
//           originalGuestConfig: {
//             adults: guestConfig.no_of_adults,
//             children: guestConfig.no_of_children,
//             roomNo: roomNumber,
//             childAges: guestConfig.child_ages || [],
//           },
//           savings: originalPrice - roomPrice,
//           guestDisplay: `${guestConfig.no_of_adults} Adult${guestConfig.no_of_adults > 1 ? 's' : ''}${guestConfig.no_of_children > 0 ? `, ${guestConfig.no_of_children} Child${guestConfig.no_of_children > 1 ? 'ren' : ''}` : ''}`,
//           ratePlan: {
//             id: rate.id,
//             name: rate.name,
//             price: rate.price,
//             offer: rate.offer
//           },
//           taxData: taxData?.taxData || null,
//           taxPerRoom: taxData?.taxData?.per_night_tax || 0,
//           taxPercentage: taxData?.taxData ? 
//             ((taxData.taxData.per_night_tax / taxData.taxData.per_night_price) * 100) : 18,
//           discountId: discountId,
//           hasDiscount: hasDiscount,
//           isFreeNightOffer: isFreeNightOffer,
//           freeNights: freeNights,
//           payableNights: Math.max(nights - freeNights, 0),
//           nights: nights
//         };
        
//         sessionData.cart.push(newCartItem);
//       });
//     } catch (error) {
//       console.error('Error calculating taxes:', error);
//       alert('Error calculating taxes. Please try again.');
//       return;
//     }
//   }

//   dispatch({ type: 'UPDATE_CART', payload: sessionData.cart });
  
//   // Update the calculateTotalTaxesForCart function
//   const calculateTotalTaxesForCart = async () => {
//     try {
//       const nights = calculateNights();
      
//       // Aggregate tax data from all rooms in cart
//       let cumulativeTotals = {
//         base_amount_total: 0,
//         total_cgst_amount_after_tax: 0,
//         total_sgst_amount_after_tax: 0,
//         total_cess_amount: 0,
//         grand_total: 0,
//         perNightBaseTotal: 0,
//         totalTaxPerRoom: 0
//       };
      
//       // Use stored tax data from each room
//       sessionData.cart.forEach((item) => {
//         if (item.taxData) {
//           cumulativeTotals.base_amount_total += item.taxData.base_amount_total;
//           cumulativeTotals.total_cgst_amount_after_tax += item.taxData.total_cgst_amount_after_tax;
//           cumulativeTotals.total_sgst_amount_after_tax += item.taxData.total_sgst_amount_after_tax;
//           cumulativeTotals.total_cess_amount += item.taxData.total_cess_amount;
//           cumulativeTotals.grand_total += item.taxData.grand_total;
//           cumulativeTotals.perNightBaseTotal += item.roomPrice;
//           cumulativeTotals.totalTaxPerRoom += (item.taxPerRoom || 0);
//         }
//       });
      
//       // Calculate rate total using totalForStay for free nights
//       const rateTotal = sessionData.cart.reduce((sum, item) => {
//         if (item.totalForStay) {
//           return sum + item.totalForStay;
//         } else {
//           return sum + (item.roomPrice * nights);
//         }
//       }, 0);
      
//       const totalTaxes = cumulativeTotals.total_cgst_amount_after_tax + 
//                         cumulativeTotals.total_sgst_amount_after_tax + 
//                         cumulativeTotals.total_cess_amount;
      
//       dispatch({
//         type: 'UPDATE_TOTALS',
//         payload: {
//           rateTotal: rateTotal,
//           subtotal: cumulativeTotals.base_amount_total,
//           taxes: totalTaxes,
//           total: cumulativeTotals.grand_total,
//           finalTotal: cumulativeTotals.grand_total,
//           taxBreakdown: {
//             cgst: cumulativeTotals.total_cgst_amount_after_tax,
//             sgst: cumulativeTotals.total_sgst_amount_after_tax,
//             cess: cumulativeTotals.total_cess_amount,
//             totalTaxPercentage: totalTaxes > 0 ? ((totalTaxes / cumulativeTotals.base_amount_total) * 100).toFixed(2) : '0.00',
//             nights: nights,
//             perNightBaseTotal: cumulativeTotals.perNightBaseTotal,
//             totalTaxPerRoom: cumulativeTotals.totalTaxPerRoom
//           }
//         }
//       });

//     } catch (error) {
//       console.error('Total tax calculation error:', error);
//       // Fallback calculation
//       const nights = calculateNights();
//       const rateTotal = sessionData.cart.reduce((sum, item) => {
//         if (item.totalForStay) {
//           return sum + item.totalForStay;
//         } else {
//           return sum + (item.roomPrice * nights);
//         }
//       }, 0);
//       const subtotal = rateTotal;
//       const taxes = subtotal * 0.18;
//       const total = subtotal + taxes;
      
//       dispatch({
//         type: 'UPDATE_TOTALS',
//         payload: {
//           rateTotal,
//           subtotal,
//           taxes,
//           total,
//           finalTotal: total,
//           taxBreakdown: null
//         }
//       });
//     }
//   };
  
//   await calculateTotalTaxesForCart();
// };

const handleRateAction = async (action) => {
  const { checkin, checkout } = state.searchState;
  if (!checkin || !checkout || checkout <= checkin) {
    alert("Please select valid Check-in and Check-out dates first.");
    return;
  }

  const sessionData = { ...state };
  const nights = calculateNights();
  const isFreeNightOffer = rate.offer && rate.offer.discount_form === 'free_nights';
  const freeNights = rate.offer?.no_free_nights || 0;
  const minStayNights = rate.offer?.min_stay_nights || 1;

  // Get room capacity constraints
  const maxAdultsAllowed = room.no_adults_allowed || 2;
  const maxChildrenAllowed = room.no_childs_allowed || 2;
  const maxGuests = room.maxGuests || (maxAdultsAllowed + maxChildrenAllowed);
  // console.log("Selected MaxChild", maxChildrenAllowed);

  // UPDATED: Modified tax calculation function to handle free nights
const calculateTaxForRoom = async (roomInfo) => {
  try {
    const formatDate = (date) => {
      if (date instanceof Date) {
        return date.toISOString().split('T')[0];
      }
      return new Date(date).toISOString().split('T')[0];
    };

    let amountForTaxCalculation = roomInfo.roomPrice;
    
    if (roomInfo.isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
      // For free nights, roomPrice is already the average per night
      // So tax calculation should use that directly
      amountForTaxCalculation = roomInfo.roomPrice;  // This is 500
      
      // console.log(`Free night tax - Using roomPrice: ${amountForTaxCalculation}`);
    }

    const payload = {
      type: "tariff",
      amount: amountForTaxCalculation,
      check_in_date: formatDate(checkin),
      check_out_date: formatDate(checkout),
      id_shop: SHOP_ID 
    };

    // console.log(`Tax calculation payload:`, payload);

      const response = await fetch(`${API_BASE_URL}/fe/mstCalculation`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
           'X-Shop-Code': SHOP_CODE,
        },
        body: JSON.stringify(payload)
      });

    const data = await response.json();
    
    if (data.total_tax) {
      const totalTax = data.total_tax;
      
      let per_night_tax = 0;
      let per_night_price = amountForTaxCalculation;
      
      const totalTaxAmount = (totalTax.total_cgst_amount_after_tax || 0) + 
                            (totalTax.total_sgst_amount_after_tax || 0) + 
                            (totalTax.total_cess_amount || 0);
      per_night_tax = totalTaxAmount / nights;
      
      return {
        roomName: roomInfo.roomName || 'Room',
        roomPrice: roomInfo.roomPrice,
        originalPrice: roomInfo.originalPrice,
        isFreeNightOffer: roomInfo.isFreeNightOffer,
        taxData: {
          base_amount_total: totalTax.base_amount_total || 0,
          total_cgst_amount_after_tax: totalTax.total_cgst_amount_after_tax || 0,
          total_sgst_amount_after_tax: totalTax.total_sgst_amount_after_tax || 0,
          total_cess_amount: totalTax.total_cess_amount || 0,
          grand_total: totalTax.grand_total || 0,
          per_night_tax: per_night_tax,
          per_night_price: per_night_price,
          daily_breakdown: data.daily_breakdown || []
        }
      };
    }
    return null;
  } catch (error) {
    console.error('Tax calculation error for single room:', error);
    return null;
  }
};

  // Calculate tax for each new room
  const taxPromises = [];
  const newRoomsInfo = [];

  if (action === 'select' || action === 'plus') {
    // Determine how many rooms to add
    let roomsToAdd = 0;
    let startIndex = 0;
    
    if (action === 'select') {
      if (allRequiredRoomsSelected) {
        alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
        return;
      }

      const roomsNeeded = state.searchState.room_guest.length;
      const roomsAlreadySelected = getCurrentRoomCountForThisCategory();
      
      roomsToAdd = Math.min(
        roomsNeeded,
        totalRoomsAvailable - roomsAlreadySelected,
        roomsStillNeeded
      );
      
      startIndex = roomsAlreadySelected;

      if (roomsToAdd <= 0) {
        if (roomsAlreadySelected >= totalRoomsAvailable) {
          alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
        } else if (allRequiredRoomsSelected) {
          alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
        }
        return;
      }

    } else { // action === 'plus'
      if (allRequiredRoomsSelected) {
        alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
        return;
      }

      if (isCategoryLimitReached) {
        alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
        return;
      }

      roomsToAdd = 1;
      startIndex = getCurrentRoomCountForThisRate();
    }

    // CRITICAL FIX: Get ALL cart items to determine which room numbers are already taken
    const allCartItems = state.cart;
    const takenRoomNumbers = new Set(allCartItems.map(item => item.roomNumber));
    
    // Find available room numbers
    let availableRoomNumbers = [];
    for (let i = 0; i < state.searchState.room_guest.length; i++) {
      const roomNum = i + 1;
      if (!takenRoomNumbers.has(roomNum)) {
        availableRoomNumbers.push(roomNum);
      }
    }
    
    // Sort ascending to assign lowest available numbers first
    availableRoomNumbers.sort((a, b) => a - b);
    
    // Take the first 'roomsToAdd' available room numbers
    const roomNumbersToAssign = availableRoomNumbers.slice(0, roomsToAdd);

    // --- CAPACITY VALIDATION: Check each room's guest count against capacity BEFORE adding ---
    const maxAdultsAllowed = Number(room.no_adults_allowed) || 0;
    const maxChildrenAllowed = Number(room.no_childs_allowed) || 0;
    const maxExtraBedsAllowed = Number(room.no_extrabeds_allowed) || 0;

    const maxTotalAdults = maxAdultsAllowed + maxExtraBedsAllowed;
    const maxTotalChildren = maxChildrenAllowed + maxExtraBedsAllowed;
    const maxTotalGuests = maxAdultsAllowed + maxChildrenAllowed + maxExtraBedsAllowed;

    // console.log('🔍 CAPACITY DEBUG:', {
    //   roomName: room.category_name || room.name,
    //   no_adults_allowed: room.no_adults_allowed,
    //   no_childs_allowed: room.no_childs_allowed,
    //   no_extrabeds_allowed: room.no_extrabeds_allowed,
    //   maxAdultsAllowed,
    //   maxChildrenAllowed,
    //   maxExtraBedsAllowed,
    //   maxTotalAdults,
    //   maxTotalChildren,
    //   maxTotalGuests
    // });

    let validationErrors = [];

    for (let i = 0; i < roomNumbersToAssign.length; i++) {
      const roomNumber = roomNumbersToAssign[i];
      const roomIndex = roomNumber - 1;
      
      const guestConfig = state.searchState.room_guest[roomIndex];
      
      if (!guestConfig) {
        console.error(`No guest config found for room ${roomNumber}`);
        continue;
      }

      const selectedAdults = guestConfig.no_of_adults;
      const selectedChildren = guestConfig.no_of_children;
      const totalSelectedGuests = selectedAdults + selectedChildren;
      
      // console.log(`📊 Room ${roomNumber} Selection:`, {
      //   selectedAdults,
      //   selectedChildren,
      //   totalSelectedGuests,
      //   maxAdultsAllowed,
      //   maxChildrenAllowed,
      //   maxExtraBedsAllowed,
      //   maxTotalAdults,
      //   maxTotalChildren,
      //   maxTotalGuests
      // });

      if (totalSelectedGuests > maxTotalGuests) {
        validationErrors.push({
          roomNumber,
          adults: selectedAdults,
          children: selectedChildren,
          total: totalSelectedGuests,
          maxTotalGuests,
          errorType: 'totalGuests'
        });
        continue;
      }
      
      if (selectedAdults > maxTotalAdults) {
        validationErrors.push({
          roomNumber,
          adults: selectedAdults,
          children: selectedChildren,
          maxTotalAdults,
          errorType: 'adults'
        });
        continue;
      }
      
      if (selectedChildren > maxTotalChildren) {
        validationErrors.push({
          roomNumber,
          adults: selectedAdults,
          children: selectedChildren,
          maxTotalChildren,
          errorType: 'children'
        });
        continue;
      }
      
      const extraAdultsNeeded = Math.max(0, selectedAdults - maxAdultsAllowed);
      const extraChildrenNeeded = Math.max(0, selectedChildren - maxChildrenAllowed);
      const totalExtraBedsNeeded = extraAdultsNeeded + extraChildrenNeeded;
      
      if (totalExtraBedsNeeded > maxExtraBedsAllowed) {
        validationErrors.push({
          roomNumber,
          adults: selectedAdults,
          children: selectedChildren,
          extraAdultsNeeded,
          extraChildrenNeeded,
          totalExtraBedsNeeded,
          maxExtraBedsAllowed,
          errorType: 'extraBeds'
        });
        continue;
      }
      
      // console.log(`✅ Room ${roomNumber} capacity validation passed`);
    }

    if (validationErrors.length > 0) {
      const error = validationErrors[0];
      let errorMessage = '';
      
      switch (error.errorType) {
        case 'totalGuests':
          errorMessage = `Room can accommodate maximum ${maxTotalGuests} total guests (including extra beds).`;
          break;
        case 'adults':
          errorMessage = `Maximum ${maxTotalAdults} adults allowed in this room (including extra beds).`;
          break;
        case 'children':
          errorMessage = `Maximum ${maxTotalChildren} children allowed in this room (including extra beds).`;
          break;
        case 'extraBeds':
          errorMessage = `Need ${error.totalExtraBedsNeeded} extra bed(s) but only ${maxExtraBedsAllowed} available.`;
          break;
        default:
          errorMessage = 'Guest configuration exceeds room capacity.';
      }
      
      onCapacityError({
        roomName: room.category_name || room.name,
        roomNumber: error.roomNumber,
        adults: error.adults,
        children: error.children,
        maxAdultsAllowed,
        maxChildrenAllowed,
        maxExtraBedsAllowed,
        baseAdults: maxAdultsAllowed,
        baseChildren: maxChildrenAllowed,
        extraBeds: maxExtraBedsAllowed,
        totalAdultsAllowed: maxTotalAdults,
        totalChildrenAllowed: maxTotalChildren,
        totalGuestsAllowed: maxTotalGuests,
        extraAdultsNeeded: error.extraAdultsNeeded || 0,
        extraChildrenNeeded: error.extraChildrenNeeded || 0,
        totalExtraBedsNeeded: error.totalExtraBedsNeeded || 0,
        errorType: error.errorType,
        errorMessage
      });
      
      return;
    }

    // console.log(`✅ Capacity validation passed for all ${roomNumbersToAssign.length} rooms`);

    // If validation passed, proceed with adding rooms
    for (let i = 0; i < roomNumbersToAssign.length; i++) {
      const roomNumber = roomNumbersToAssign[i];
      const roomIndex = roomNumber - 1;
      
      const guestConfig = state.searchState.room_guest[roomIndex];
      
      if (!guestConfig) {
        console.error(`No guest config found for room ${roomNumber}`);
        continue;
      }

      // console.log(`Assigning room ${roomNumber} with guest config:`, guestConfig);

      // *** FIX: Use simplified pricing for free night offers ***
      let pricing;
      if (isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
        // For free night offers: use pre-calculated values from API
        pricing = {
          discountedPrice: rate.price,      // This is the AVERAGE per night (500)
          originalPrice: rate.originalPrice, // This is the base price (1000)
          hasDiscount: rate.originalPrice > rate.price
        };
        // console.log(`Free night offer - Using rate.price: ${rate.price}, rate.originalPrice: ${rate.originalPrice}`);
      } else {
        // Regular calculation for non-free-night offers
        pricing = getPricingForGuests(
          guestConfig.no_of_adults, 
          guestConfig.no_of_children,
          guestConfig.child_ages || []
        );
      }
      
      const roomInfo = {
        roomNumber: roomNumber,
        roomIndex: roomIndex,
        guestConfig,
        roomPrice: pricing.discountedPrice,
        originalPrice: pricing.originalPrice,
        roomId: room.id,
        roomName: room.name,
        rateId: rate.id,
        hasDiscount: pricing.hasDiscount,
        isFreeNightOffer: isFreeNightOffer && nights >= minStayNights && freeNights > 0
      };
      
      newRoomsInfo.push(roomInfo);
      taxPromises.push(calculateTaxForRoom(roomInfo));
    }

  } else if (action === 'minus') {
    const rateItems = getCartItemsForThisRate();
    
    if (rateItems.length > 0) {
      const highestRoomItem = rateItems.reduce((prev, current) => 
        (prev.roomNumber > current.roomNumber) ? prev : current
      );
      
      const indexToRemove = sessionData.cart.findIndex(item => 
        item.id === highestRoomItem.id && 
        item.roomId === highestRoomItem.roomId && 
        item.roomNumber === highestRoomItem.roomNumber
      );
      
      if (indexToRemove > -1) {
        sessionData.cart.splice(indexToRemove, 1);

       

// gtm:
if (window.dataLayer) {
  window.dataLayer.push({
    event: 'remove_from_cart',
    ecommerce: {
      currency: 'INR',
      items: [{
        item_id: room.id,
        item_name: room.name,
        item_category: rate.name,
        price: highestRoomItem.roomPrice,
        quantity: 1
      }]
    }
  });
}
      }
    }
  }

  // If we're adding rooms (select or plus), wait for tax calculations
  if ((action === 'select' || action === 'plus') && taxPromises.length > 0) {
    try {
      const taxResults = await Promise.all(taxPromises);
      
      newRoomsInfo.forEach((roomInfo, index) => {
        const { roomNumber, guestConfig, roomPrice, originalPrice, roomId, roomName, rateId, hasDiscount, isFreeNightOffer } = roomInfo;
        const basePrice = rate.base_price || rate.originalPrice || roomPrice;
        const taxData = taxResults[index];
        const discountId = rate.offer?.id || null;
        
        let totalForStay = 0;
        let originalTotalForStay = 0;
        let averagePricePerNight = roomPrice;
        
        if (isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
  // For free nights, the total is simply average price × nights
  totalForStay = roomPrice * nights;  // 500 × 3 = 1500
  originalTotalForStay = originalPrice * nights;  // 1000 × 3 = 3000
  averagePricePerNight = roomPrice;  // 500
} else {
  totalForStay = roomPrice * nights;
  originalTotalForStay = originalPrice * nights;
  averagePricePerNight = roomPrice;
}
        
        const newCartItem = {
          id: rate.id,
          roomId: roomId,
          roomNumber: roomNumber,
          roomName: roomName,
          rateName: rate.name,
          roomPrice: roomPrice,
          originalPrice: originalPrice,
          averagePricePerNight: averagePricePerNight,
          totalForStay: totalForStay,
          originalTotalForStay: originalTotalForStay,
          basePrice: basePrice,
          adultsCount: guestConfig.no_of_adults,
          childrenCount: guestConfig.no_of_children,
          childAges: guestConfig.child_ages || [],
          imageUrl: room.images[0],
          discountInfo: discountInfo ? {
            name: discountInfo.name,
            type: discountInfo.type,
            value: discountInfo.value,
            rawValue: discountInfo.rawValue,
            discountId: discountId
          } : null,
          offerName: discountInfo ? discountInfo.name : (rate.offer?.discount_name || null),
          offerFreeNights: rate.offerFreeNights || 0,
          quantity: 1,
          guestInfo: {
            adults: guestConfig.no_of_adults,
            children: guestConfig.no_of_children,
            childAges: guestConfig.child_ages || [], 
          },
          originalGuestConfig: {
            adults: guestConfig.no_of_adults,
            children: guestConfig.no_of_children,
            roomNo: roomNumber,
            childAges: guestConfig.child_ages || [],
          },
          savings: originalPrice - roomPrice,
          guestDisplay: `${guestConfig.no_of_adults} Adult${guestConfig.no_of_adults > 1 ? 's' : ''}${guestConfig.no_of_children > 0 ? `, ${guestConfig.no_of_children} Child${guestConfig.no_of_children > 1 ? 'ren' : ''}` : ''}`,
          ratePlan: {
            id: rate.id,
            name: rate.name,
            price: rate.price,
            offer: rate.offer
          },
          taxData: taxData?.taxData || null,
          taxPerRoom: taxData?.taxData?.per_night_tax || 0,
          taxPercentage: taxData?.taxData ? 
            ((taxData.taxData.per_night_tax / taxData.taxData.per_night_price) * 100) : 18,
          discountId: discountId,
          hasDiscount: hasDiscount,
          isFreeNightOffer: isFreeNightOffer,
          freeNights: freeNights,
          payableNights: Math.max(nights - freeNights, 0),
          nights: nights
        };
        
        sessionData.cart.push(newCartItem);

        // Around line where sessionData.cart.push(newCartItem) is called
// sessionData.cart.push(newCartItem);

// ADD THIS RIGHT AFTER:
if (window.dataLayer) {
  window.dataLayer.push({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'INR',
      value: newCartItem.roomPrice,
      items: [{
        item_id: room.id,
        item_name: room.name,
        item_category: rate.name,
        price: newCartItem.roomPrice,
        quantity: 1
      }]
    }
  });
}


      });
    } catch (error) {
      console.error('Error calculating taxes:', error);
      alert('Error calculating taxes. Please try again.');
      return;
    }
  }

  dispatch({ type: 'UPDATE_CART', payload: sessionData.cart });
  
  const calculateTotalTaxesForCart = async () => {
    try {
      const nights = calculateNights();
      
      let cumulativeTotals = {
        base_amount_total: 0,
        total_cgst_amount_after_tax: 0,
        total_sgst_amount_after_tax: 0,
        total_cess_amount: 0,
        grand_total: 0,
        perNightBaseTotal: 0,
        totalTaxPerRoom: 0
      };
      
      sessionData.cart.forEach((item) => {
        if (item.taxData) {
          cumulativeTotals.base_amount_total += item.taxData.base_amount_total;
          cumulativeTotals.total_cgst_amount_after_tax += item.taxData.total_cgst_amount_after_tax;
          cumulativeTotals.total_sgst_amount_after_tax += item.taxData.total_sgst_amount_after_tax;
          cumulativeTotals.total_cess_amount += item.taxData.total_cess_amount;
          cumulativeTotals.grand_total += item.taxData.grand_total;
          cumulativeTotals.perNightBaseTotal += item.roomPrice;
          cumulativeTotals.totalTaxPerRoom += (item.taxPerRoom || 0);
        }
      });
      
      const rateTotal = sessionData.cart.reduce((sum, item) => {
        if (item.totalForStay) {
          return sum + item.totalForStay;
        } else {
          return sum + (item.roomPrice * nights);
        }
      }, 0);
      
      const totalTaxes = cumulativeTotals.total_cgst_amount_after_tax + 
                        cumulativeTotals.total_sgst_amount_after_tax + 
                        cumulativeTotals.total_cess_amount;
      
      dispatch({
        type: 'UPDATE_TOTALS',
        payload: {
          rateTotal: rateTotal,
          subtotal: cumulativeTotals.base_amount_total,
          taxes: totalTaxes,
          total: cumulativeTotals.grand_total,
          finalTotal: cumulativeTotals.grand_total,
          taxBreakdown: {
            cgst: cumulativeTotals.total_cgst_amount_after_tax,
            sgst: cumulativeTotals.total_sgst_amount_after_tax,
            cess: cumulativeTotals.total_cess_amount,
            totalTaxPercentage: totalTaxes > 0 ? ((totalTaxes / cumulativeTotals.base_amount_total) * 100).toFixed(2) : '0.00',
            nights: nights,
            perNightBaseTotal: cumulativeTotals.perNightBaseTotal,
            totalTaxPerRoom: cumulativeTotals.totalTaxPerRoom
          }
        }
      });

    } catch (error) {
      console.error('Total tax calculation error:', error);
      const nights = calculateNights();
      const rateTotal = sessionData.cart.reduce((sum, item) => {
        if (item.totalForStay) {
          return sum + item.totalForStay;
        } else {
          return sum + (item.roomPrice * nights);
        }
      }, 0);
      const subtotal = rateTotal;
      const taxes = subtotal * 0.18;
      const total = subtotal + taxes;
      
      dispatch({
        type: 'UPDATE_TOTALS',
        payload: {
          rateTotal,
          subtotal,
          taxes,
          total,
          finalTotal: total,
          taxBreakdown: null
        }
      });
    }
  };
  
  await calculateTotalTaxesForCart();
};

  return (
  <div className="rate-option-row group border-t border-gray-100 first:border-t-0 py-4 px-2 sm:px-4 transition-all duration-150 hover:bg-slate-50/50">
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
      
     {/* 1. Left Side: Rate Name and Inclusions */}
<div className="flex-grow pr-4">
  <p className="text-[16px] font-bold text-gray-900 tracking-tight leading-snug">
    {rate.name}
  </p>

  {inclusions && inclusions.length > 0 && (
    <div className="mt-3 space-y-2.5">
      {inclusions.map((inc, index) => (
        <div key={index} className="flex items-center group">
          {/* Enhanced Icon Container */}
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center mr-3 transition-colors group-hover:bg-emerald-100">
            <svg
              className="w-3 h-3 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          </div>
          
          <span className="text-[13px] font-medium text-gray-600 group-hover:text-gray-900 transition-colors">
            {inc}
          </span>
        </div>
      ))}
    </div>
  )}
</div>
      
      {/* 2. Right/Bottom Side: Pricing and Counter */}
      <div className="flex items-center sm:items-end justify-between sm:justify-start gap-4 pt-3 sm:pt-0 border-t border-dashed border-gray-100 sm:border-0">
        
        {/* Pricing Column */}
        <div className="flex flex-col items-start sm:items-end">
          <div className="flex items-center gap-2">
             {displayPriceData.hasDiscount && displayPriceData.totalOriginalPrice > 0 && (
              <span className="text-xs text-gray-600 line-through decoration-red-300">
                {formatCurrency(displayPriceData.totalOriginalPrice)}
              </span>
            )}
            <p className="text-lg font-extrabold text-gray-900">
              {formatCurrency(displayPriceData.totalPrice)}
            </p>
          </div>
          <span className="text-[11px] text-gray-600 mt-0.5">
            Estimate for {displayPriceData.roomCount} room{displayPriceData.roomCount > 1 ? 's' : ''} / night
          </span>
        </div>
        
        {/* Action/Counter Column */}
        <div className="w-28 flex justify-end">
          {currentRoomCountForThisRate === 0 ? (
            <button
              onClick={() => handleRateAction('select')}
              disabled={allRequiredRoomsSelected || isCategoryLimitReached}
              className={`w-full bg-brand-accent text-dark py-2 rounded-lg font-bold text-sm shadow-sm active:scale-95 transition-all ${
                (allRequiredRoomsSelected || isCategoryLimitReached) ? 'opacity-40 grayscale cursor-not-allowed' : 'hover:brightness-110'
              }`}
            >
              Select
            </button>
          ) : (
            <div className="flex items-center bg-gray-100 rounded-full p-1 border border-gray-200">
              <button
                onClick={() => handleRateAction('minus')}
                className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-700 active:bg-gray-200"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M20 12H4" /></svg>
              </button>
              <span className="font-bold text-sm text-gray-800 w-8 text-center">
                {currentRoomCountForThisRate}
              </span>
              <button
                onClick={() => handleRateAction('plus')}
                disabled={allRequiredRoomsSelected || isCategoryLimitReached}
                className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-700 active:bg-gray-200 disabled:opacity-30"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" /></svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);
};

export default RateOption;